/**
 * Resources API Client
 * Strapi integration for resources management
 */

import { getHttpClient } from '@ufc/core'
import type { Resource, ResourcesFilter, ResourceStatus, PaginatedResources, PaginationMeta } from '../../domain/types'
import { resourceStatusToBackend, DEFAULT_PAGE_SIZE } from '../../domain/types'
import {
  STRAPI_RESOURCE_ENDPOINTS,
  type StrapiResourceListResponse,
  type StrapiResourceResponse,
  strapiResourceListResponseSchema,
  strapiResourceResponseSchema,
  type DeactivationResult,
  type EntityUpdateResult,
} from '../../api/api-types'
import {
  transformResourceToStrapiCreate,
  transformResourceToStrapiUpdate,
  transformStrapiResourceToDomain,
  transformStrapiResourcesToDomain,
} from '../../api/transformers'

const RESOURCE_ASSET_FIELDS = [
  'documentId',
  'assetNumber',
  'licensePlate',
  'make',
  'model',
  'year',
  'vin',
  'assetStatus',
  'capacity',
  'capacityUnit',
  'description',
  'metadata',
  'createdAt',
  'updatedAt',
] as const

const RESOURCE_DRIVER_FIELDS = [
  'documentId',
  'name',
  'licenseNumber',
  'licenseType',
  'licenseExpiry',
  'nationality',
  'phone',
  'driverStatus',
  'metadata',
  'deletedAt',
  'createdAt',
  'updatedAt',
] as const

function applyPopulateFields(params: URLSearchParams, relation: string, fields: readonly string[]) {
  fields.forEach((field, index) => {
    params.set(`populate[${relation}][fields][${index}]`, field)
  })
}

export const resourcesApi = {
  /**
   * Get all resources with optional filtering
   */
  async getResources(filter?: ResourcesFilter): Promise<Resource[]> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    if (filter?.page) {
      params.set('pagination[page]', String(filter.page))
    }
    if (filter?.pageSize) {
      params.set('pagination[pageSize]', String(filter.pageSize))
    }

    if (filter?.status && filter.status !== 'all') {
      const backendStatus = resourceStatusToBackend[filter.status as ResourceStatus]
      params.set('filters[resourceStatus][$eq]', backendStatus)
    }

    if (filter?.search) {
      params.set('filters[$or][0][name][$containsi]', filter.search)
      params.set('filters[$or][1][description][$containsi]', filter.search)
      params.set('filters[$or][2][driver][name][$containsi]', filter.search)
      params.set('filters[$or][3][driver][phone][$containsi]', filter.search)
      params.set('filters[$or][4][powerUnit][licensePlate][$containsi]', filter.search)
      params.set('filters[$or][5][powerUnit][assetNumber][$containsi]', filter.search)
    }

    // Driver name filter (autocomplete search)
    if (filter?.driverName) {
      params.set('filters[driver][name][$containsi]', filter.driverName)
    }

    // Vehicle license plate filter
    if (filter?.vehicleLicensePlate) {
      params.set('filters[powerUnit][licensePlate][$containsi]', filter.vehicleLicensePlate)
    }

    // Driver phone filter
    if (filter?.driverPhone) {
      params.set('filters[driver][phone][$containsi]', filter.driverPhone)
    }

    params.set('populate[tenant]', '*')
    applyPopulateFields(params, 'driver', RESOURCE_DRIVER_FIELDS)
    applyPopulateFields(params, 'powerUnit', RESOURCE_ASSET_FIELDS)
    applyPopulateFields(params, 'trailer', RESOURCE_ASSET_FIELDS)

    const url = `${STRAPI_RESOURCE_ENDPOINTS.LIST}?${params.toString()}`
    const response = await http.get<StrapiResourceListResponse>(url)

    try {
      const validated = strapiResourceListResponseSchema.parse(response.data)
      let data = transformStrapiResourcesToDomain(validated.data)
      if (filter?.powerUnitVehicleType) {
        data = data.filter(r => r.vehicle?.type === filter.powerUnitVehicleType)
      }
      return data
    } catch (error) {
      console.error('Strapi response validation error (resources/list):', error)
      console.error('Response data:', response.data)
      if (response.data?.data && Array.isArray(response.data.data)) {
        let data = transformStrapiResourcesToDomain(response.data.data as any)
        if (filter?.powerUnitVehicleType) {
          data = data.filter(r => r.vehicle?.type === filter.powerUnitVehicleType)
        }
        return data
      }
      throw error
    }
  },

  /**
   * Get resources with pagination
   */
  async getResourcesPaginated(filter?: ResourcesFilter): Promise<PaginatedResources> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    // Pagination
    const page = filter?.page ?? 1
    const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE
    params.set('pagination[page]', String(page))
    params.set('pagination[pageSize]', String(pageSize))

    // Status filter - supports single value or array
    if (filter?.status && filter.status !== 'all') {
      if (Array.isArray(filter.status)) {
        // Multiple statuses - use $in operator
        filter.status.forEach((status, index) => {
          const backendStatus = resourceStatusToBackend[status]
          params.set(`filters[resourceStatus][$in][${index}]`, backendStatus)
        })
      } else {
        // Single status
        const backendStatus = resourceStatusToBackend[filter.status]
        params.set('filters[resourceStatus][$eq]', backendStatus)
      }
    }

    // Driver name filter (autocomplete search)
    if (filter?.driverName) {
      params.set('filters[driver][name][$containsi]', filter.driverName)
    }

    // Vehicle license plate filter
    if (filter?.vehicleLicensePlate) {
      params.set('filters[powerUnit][licensePlate][$containsi]', filter.vehicleLicensePlate)
    }

    // Driver phone filter
    if (filter?.driverPhone) {
      params.set('filters[driver][phone][$containsi]', filter.driverPhone)
    }

    if (filter?.search) {
      params.set('filters[$or][0][name][$containsi]', filter.search)
      params.set('filters[$or][1][description][$containsi]', filter.search)
      params.set('filters[$or][2][driver][name][$containsi]', filter.search)
      params.set('filters[$or][3][driver][phone][$containsi]', filter.search)
      params.set('filters[$or][4][powerUnit][licensePlate][$containsi]', filter.search)
      params.set('filters[$or][5][powerUnit][assetNumber][$containsi]', filter.search)
    }

    // Scope to a specific tenant when provided (prevents cross-tenant leakage in admin portal)
    if (filter?.tenantDocumentId) {
      params.set('filters[tenant][documentId][$eq]', filter.tenantDocumentId)
    }

    // Sorting - map frontend field names to Strapi field names
    if (filter?.sortBy) {
      const sortFieldMap: Record<string, string> = {
        name: 'name',
        status: 'resourceStatus',
        assignedSince: 'assignedSince',
        createdAt: 'createdAt',
      }
      const strapiField = sortFieldMap[filter.sortBy] || filter.sortBy
      const sortDirection = filter.sortOrder === 'desc' ? ':desc' : ':asc'
      params.set('sort[0]', `${strapiField}${sortDirection}`)
    } else {
      // Default sort by name ascending
      params.set('sort[0]', 'name:asc')
    }

    params.set('populate[tenant]', '*')
    applyPopulateFields(params, 'driver', RESOURCE_DRIVER_FIELDS)
    applyPopulateFields(params, 'powerUnit', RESOURCE_ASSET_FIELDS)
    applyPopulateFields(params, 'trailer', RESOURCE_ASSET_FIELDS)

    const url = `${STRAPI_RESOURCE_ENDPOINTS.LIST}?${params.toString()}`
    const response = await http.get<StrapiResourceListResponse>(url)

    // Extract pagination from response
    const meta = (response.data as any)?.meta?.pagination
    const pagination: PaginationMeta = {
      page: meta?.page ?? page,
      pageSize: meta?.pageSize ?? pageSize,
      pageCount: meta?.pageCount ?? 1,
      total: meta?.total ?? 0,
    }

    try {
      const validated = strapiResourceListResponseSchema.parse(response.data)
      let data = transformStrapiResourcesToDomain(validated.data)
      if (filter?.powerUnitVehicleType) {
        data = data.filter(r => r.vehicle?.type === filter.powerUnitVehicleType)
      }
      return { data, pagination }
    } catch (error) {
      console.error('Strapi response validation error (resources/list paginated):', error)
      if (response.data?.data && Array.isArray(response.data.data)) {
        let data = transformStrapiResourcesToDomain(response.data.data as any)
        if (filter?.powerUnitVehicleType) {
          data = data.filter(r => r.vehicle?.type === filter.powerUnitVehicleType)
        }
        return { data, pagination }
      }
      throw error
    }
  },

  /**
   * Get a single resource by documentId
   */
  async getResource(documentId: string): Promise<Resource | null> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    params.set('populate[tenant]', '*')
    applyPopulateFields(params, 'driver', RESOURCE_DRIVER_FIELDS)
    applyPopulateFields(params, 'powerUnit', RESOURCE_ASSET_FIELDS)
    applyPopulateFields(params, 'trailer', RESOURCE_ASSET_FIELDS)

    const url = `${STRAPI_RESOURCE_ENDPOINTS.DETAIL(documentId)}?${params.toString()}`

    try {
      const response = await http.get<StrapiResourceResponse>(url)
      try {
        const validated = strapiResourceResponseSchema.parse(response.data)
        return transformStrapiResourceToDomain(validated.data)
      } catch (error) {
        console.error('Strapi response validation error (resources/detail):', error)
        console.error('Response data:', response.data)
        if ((response.data as any)?.data) {
          return transformStrapiResourceToDomain((response.data as any).data)
        }
        return null
      }
    } catch (error) {
      const axiosError = error as { response?: { status?: number } }
      if (axiosError.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Create a new resource
   */
  async createResource(resource: Partial<Resource> & { tenantId: string }): Promise<Resource> {
    const http = getHttpClient()
    const requestData = transformResourceToStrapiCreate(resource)

    const response = await http.post(STRAPI_RESOURCE_ENDPOINTS.CREATE, requestData)

    const respData: any = (response as any).data
    if (respData?.data) {
      try {
        const validated = strapiResourceResponseSchema.parse(respData)
        return transformStrapiResourceToDomain(validated.data)
      } catch (error) {
        console.error('Strapi response validation error (resources/create):', error)
        console.error('Response data:', respData)
        return transformStrapiResourceToDomain(respData.data)
      }
    }

    if (respData?.id && respData?.documentId) {
      return transformStrapiResourceToDomain(respData)
    }

    throw new Error('Unexpected createResource response shape')
  },

  /**
   * Update an existing resource
   */
  async updateResource(documentId: string, resource: Partial<Resource>): Promise<EntityUpdateResult<Resource>> {
    const http = getHttpClient()
    const requestData = transformResourceToStrapiUpdate(resource)

    const response = await http.put(STRAPI_RESOURCE_ENDPOINTS.UPDATE(documentId), requestData)

    const respData: any = (response as any).data
    const deactivation = {
      affectedOrders: (respData?.affectedOrders as string[]) ?? [],
      affectedResources: (respData?.affectedResources as string[]) ?? [],
    }

    let entity: Resource
    if (respData?.data) {
      try {
        const validated = strapiResourceResponseSchema.parse(respData)
        entity = transformStrapiResourceToDomain(validated.data)
      } catch (error) {
        console.error('Strapi response validation error (resources/update):', error)
        console.error('Response data:', respData)
        entity = transformStrapiResourceToDomain(respData.data)
      }
    } else if (respData?.id && respData?.documentId) {
      entity = transformStrapiResourceToDomain(respData)
    } else {
      throw new Error('Unexpected updateResource response shape')
    }

    return { entity, deactivation }
  },

  /**
   * Delete a resource
   */
  async deleteResource(documentId: string): Promise<DeactivationResult> {
    const http = getHttpClient()
    const response = await http.delete(STRAPI_RESOURCE_ENDPOINTS.DELETE(documentId))
    const respData: any = (response as any).data

    return {
      affectedOrders: (respData?.affectedOrders as string[]) ?? [],
      affectedResources: [],
    }
  },

  /**
   * Export all resources matching current filters (no pagination limit)
   */
  async exportAll(filter?: ResourcesFilter): Promise<Resource[]> {
    const http = getHttpClient()
    const params = new URLSearchParams()

    if (filter?.status && filter.status !== 'all') {
      const backendStatus = resourceStatusToBackend[filter.status as ResourceStatus]
      if (backendStatus) params.set('status', backendStatus)
    }
    if (filter?.search) params.set('search', filter.search)

    const response = await http.get<unknown>(`${STRAPI_RESOURCE_ENDPOINTS.LIST}/export?${params}`)
    const responseData = response.data as Record<string, unknown>
    const raw = Array.isArray(responseData?.data) ? responseData.data : []
    return transformStrapiResourcesToDomain(raw as Parameters<typeof transformStrapiResourcesToDomain>[0])
  },

  async bulkImport(rows: Array<Record<string, string>>): Promise<{ created: number; skipped: number; errors: string[] }> {
    const http = getHttpClient()
    const response = await http.post<unknown>(STRAPI_RESOURCE_ENDPOINTS.BULK_IMPORT, { data: rows })
    return response.data as { created: number; skipped: number; errors: string[] }
  },
}
